<script setup lang="ts">
import type { IProps } from './PartnershipsAwards.types'
import UiExpander from '@/components/UiExpander/UiExpander.vue'

defineProps<IProps>()
const variant = (index: number) => {
  return index % 2 === 1 ? 'left' : 'right'
}

const variantLast = (index: number, arrayLength: number) => {
  return index === arrayLength ? 'last' : ''
}
</script>

<template>
  <div
    id="awards"
    class="awards"
  >
    <div class="awards__container">
      <div class="awards__wrapper">
        <div class="awards__header-container">
          <UiHeader
            :header="header"
            :subheader="subheader"
            header-size="h4"
            header-size-mobile="h8"
            subheader-size="h8"
            subheader-size-mobile?="body-1"
          />
        </div>

        <div class="awards__cards">
          <div
            v-for="(card, index) in items"
            :key="card.title"
            :class="['awards__card', `${index % 2 === 1 ? 'awards__card--right' : ''}`]"
          >
            <div class="awards__card-text">
              <p class="awards__card-title">
                {{ card.title }}
              </p>

              <p class="awards__card-description">
                {{ card.description }}
              </p>

              <UiExpander
                v-if="card.descriptionLong"
                class="awards__card-expander"
                :text-to-show="$t('partnerships.awards.readMore')"
                :text-to-hide="$t('partnerships.awards.readLess')"
              >
                <div
                  class="awards__card-description"
                  v-html="card.descriptionLong"
                />
              </UiExpander>

              <div
                :class="['awards__card-lines', `awards__card-lines--${variant(index)}`, `awards__card-lines--${variantLast(index, items.length - 1)}`]"
              >
                <div :class="['awards__card-dot', `awards__card-dot--${variant(index)}`]">
                  <div class="awards__card-dot-inner" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.awards {
  width: 100%;

  :deep(.ui-header) {
    text-align: left;
  }

  :deep(.ui-header__subheader) {
    margin-bottom: 3.2rem;
    text-align: left;
    @include h9;
  }

  &__container {
    display: flex;
    justify-content: center;
    padding: 4rem 3.2rem;
    background-color: $gray-100;
    border-radius: 2rem;
  }

  &__wrapper {
    max-width: 111.6rem;
    width: 100%;
    text-align: center;
  }

  &__header-container {
    margin: 0 auto 4rem auto;
  }

  &__cards {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__card {
    margin-bottom: 0.8rem;
    padding: 0;
  }

  &__card-text {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 55.8rem;
    width: 100%;
    padding: 4rem;
    background-color: $white;
    border-radius: 2.4rem;
  }

  &__card-title {
    @include h9;
    margin-bottom: 1.6rem;
    text-align: left;
  }

  &__card-description {
    @include body-2-medium;
    text-align: left;
  }

  &__card-lines {
    position: absolute;
    left: -3.2rem;
    display: none;
    width: 3.2rem;
    height: calc(100% + 16px);

    &--left {
      border-top: 1px dashed $purple-300;
      border-left: 1px dashed $purple-300;
    }

    &--right {
      border-top: 1px dashed $purple-300;
      border-left: 1px dashed $purple-300;
    }

    &--last {
      border-right: none;
      border-left: none;
    }
  }

  &__card-dot {
    position: absolute;
    top: -1.6rem;
    left: -1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 3.2rem;
    height: 3.2rem;
    background-color: $purple-100;
    border-radius: 50%;
  }

  &__card-dot-inner {
    width: 1.6rem;
    height: 1.6rem;
    background-color: $purple-400;
    border-radius: 50%;
  }
}

:deep(.awards__card-description) {
  h4 {
    font-weight: bold;
  }
  ul {
    margin-bottom: 1.6rem;
    padding-left: 2rem;
    list-style-type: disc
  }
  li {
     @include body-2-medium;
  }
}

:deep(.uiExpander__button) {
  text-decoration: underline;
  text-decoration-color: $purple-400;
}

@include atM {

  .awards {
    padding: 0 1.6rem;

    :deep(.ui-header) {
      text-align: center;
    }

    :deep(.ui-header__subheader) {
      margin-bottom: 5.6rem;
      text-align: center;
    }

    &__container {
      padding: 12rem 0;
    }

    &__header-container {
      margin: 0 auto 7.9rem auto;
    }

    &__card {
      position: relative;
      display: flex;
    }

    &__card-lines {
      display: block;
    }

    &__card-text {
      width: 43.6rem;
      padding: 2.4rem 2rem 2.4rem 3.2rem;
    }

    &__card-title {
      @include h9;
    }
  }
}

@include atL{

  .awards {
    &__card {
      margin-bottom: -8rem;

      &:last-child {
        margin-bottom: 0;
      }

      &--right {
        justify-content: flex-end;
      }
    }

    &__cards {
      align-items: unset;
    }

    &__card-lines {
      width: 12.25rem;
      height: calc(100% - 8rem);

      &--left {
        right: unset;
        left: -12.25rem;
        border-right: none;
        border-left: 1px dashed $purple-300;
      }

      &--right {
        right: -12.25rem;
        left: unset;
        border-right: 1px dashed $purple-300;
        border-left: none;
      }

      &--last {
        border-right: none;
        border-left: none;
      }
    }

    &__card-dot {
      &--left {
        top: -1.6rem;
        right: unset;
        left: -1.6rem;
      }

      &--right {
        top: -1.6rem;
        right: -1.6rem;
        left: unset;
      }
    }
  }
}
</style>
